import PageIntro from 'components/PageIntro';
import { useQuery } from '@tanstack/react-query';
import { NavLink } from 'react-router';
import { Typography } from 'design-component';
import React from 'react';
import styled from 'styled-components';
import { palette } from 'styles/color';
import { apiWrapper } from 'api';
import { numberWithCommas, formatUtcToLocal } from 'utils/format';
import { useTranslation, Trans } from 'react-i18next';

const Usage = () => {
  const { t } = useTranslation();
  const getApiUsage = async () => {
    const res = await apiWrapper('/api/v1/datainsight/playground/apiusage', {
      method: 'GET'
    });
    const data = await res.json();

    return data.data;
  };

  const { data: apiUsage } = useQuery({
    queryKey: ['api-usage'],
    queryFn: getApiUsage,
    select: (data) => {
      return [
        {
          title: `${t('apiRateLimit')}`,
          content: data.rateLimit,
          unit: (
            <Typography size="13px" weight={400} height="150%" color={palette.gray[700]}>
              {t('apiRateLimitUnit')}
            </Typography>
          )
        },
        {
          title: `${t('apiUsageLastDay')}`,
          content: `${
            typeof data.lastDayOfAPIUsage === 'string'
              ? formatUtcToLocal(data.lastDayOfAPIUsage, 'MMMM do, yyyy')
              : '-'
          }`
        },
        {
          title: `${t('apiTotal')}`,
          content: data.total,
          unit: (
            <Typography size="13px" weight={400} height="150%" color={palette.gray[700]}>
              {t('apiTotalUnit')}
            </Typography>
          )
        }
      ];
    }
  });

  return (
    <div>
      <PageIntro title="API Usage">
        <Typography size="16px" weight={'normal'} color={palette.gray[500]}>
          <Trans
            i18nKey="apiUsageDesc"
            components={{
              1: (
                <StlyedLink
                  to="https://polarisofficehelp.zendesk.com/hc/ko/requests/new?ticket_form_id=360011205956"
                  target="blank"
                />
              )
            }}
          />
        </Typography>
      </PageIntro>
      <Container>
        {apiUsage?.map((item, index) => {
          return (
            <UsageItem key={index} title={item.title} content={item.content} unit={item.unit} />
          );
        })}
      </Container>
    </div>
  );
};

const UsageItem = ({
  title,
  content,
  unit
}: {
  title: string;
  content: string | number | null;
  unit?: React.ReactNode;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
      }}>
      <Typography size="18px" weight={600} color={palette.gray[800]} height="150%">
        {title}
      </Typography>
      <div className="usageValue">
        <Typography size="16px" weight={400} color={palette.gray[900]} height="150%">
          {typeof content === 'number' && numberWithCommas(content)}
          {typeof content === 'string' && content}
          {content === null && content}
        </Typography>
        {unit && unit}
      </div>
    </div>
  );
};

export default Usage;

export const StlyedLink = styled(NavLink)`
  display: inline-block;
  color: ${palette.blue[500]};
  font-weight: 600;

  &:hover {
    color: #1862d2;
  }
`;

const Container = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 20px;
  background: ${palette.gray[100]};
  margin-top: 40px;

  border-radius: 13px;

  .usageValue {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;
